import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { FiEdit2, FiPlus, FiTrash2 } from "react-icons/fi";
import Overlay from "../../Componentes/Overlay";
import Modal from "../../Componentes/Utils/Modal";
import { useModal } from "../../contexts/ModalContext";
import { useToast } from "../../contexts/ToastContext";
import useApi from "../../hooks/useApi";
import ModalConfirmDeleteGrupo, {
    MODAL_ID as MODAL_DELETE_GRUPO_ID,
} from "./ModalConfirmDeleteGrupo";
import ModalEditarGrupo, { MODAL_ID as MODAL_EDITAR_GRUPO_ID } from "./ModalEditarGrupo";
import { MODAL_ID as MODAL_NOVO_GRUPO_ID } from "./ModalNovoGrupo";

export const MODAL_ID = "gestaoAcessosV2GerenciarGrupos";
const LIST_GRUPOS_URL = "/Painel/API/AccessManagement/GestaoAcessos/ListGrupos/";
const LIST_MODULOS_URL = "/Painel/API/AccessManagement/GestaoAcessos/ListModulos/";

const Container = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
`;

const Titulo = styled.h2`
    margin: 0;
    font-size: 18px;
    color: #263b4b;
`;

const BotaoNovo = styled.button`
    all: unset;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background-color: #2563eb;
    color: #fff;
    padding: 8px 14px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
        background-color: #1d4ed8;
    }
`;

const TabelaWrapper = styled.div`
    width: 100%;
    max-height: 450px;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    overflow: auto;
    box-sizing: border-box;
`;

const Tabela = styled.table`
    width: 100%;
    border-collapse: collapse;
    background: #fff;

    thead tr {
        background-color: #f8fafc;
        height: 40px;
    }

    th {
        padding: 0 14px;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        text-align: left;
        color: #64748b;
        border-bottom: 1px solid #e2e8f0;
        position: sticky;
        top: 0;
        background-color: #f8fafc;
        z-index: 1;
    }

    tbody tr {
        height: 40px;

        &:nth-child(even) {
            background-color: #fafafa;
        }

        &:hover {
            background-color: #e8f0fe;
        }
    }

    td {
        padding: 0 14px;
        font-size: 13px;
        color: #1e293b;
        border-top: 1px solid #f1f5f9;
    }

    td.numero,
    th.numero {
        width: 110px;
        text-align: center;
    }

    td.modulo,
    th.modulo {
        width: 140px;
    }

    td.acoes,
    th.acoes {
        width: 90px;
        text-align: center;
    }
`;

const AcoesCelula = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
`;

const BotaoAcao = styled.button`
    all: unset;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease;

    color: ${(p) => (p.$danger ? "#b91c1c" : "#1d4ed8")};
    background-color: ${(p) =>
        p.$danger ? "rgba(185, 28, 28, 0.12)" : "rgba(29, 78, 216, 0.10)"};
    border: 1px solid transparent;

    &:hover {
        background-color: ${(p) =>
            p.$danger ? "rgba(185, 28, 28, 0.22)" : "rgba(29, 78, 216, 0.20)"};
        border-color: ${(p) =>
            p.$danger ? "rgba(185, 28, 28, 0.5)" : "rgba(29, 78, 216, 0.45)"};
    }

    &:focus-visible {
        outline: 2px solid ${(p) => (p.$danger ? "#b91c1c" : "#1d4ed8")};
        outline-offset: 2px;
    }
`;

const Vazio = styled.div`
    padding: 28px 16px;
    text-align: center;
    color: #64748b;
    font-size: 13px;
`;

const ModalGerenciarGrupos = ({ onSaved }) => {
    const api = useApi();
    const { closeModal, openModal, isModalOpen } = useModal();
    const { notifyError } = useToast();

    const [loading, setLoading] = useState(true);
    const [grupos, setGrupos] = useState([]);
    const [modulosGerenciaveis, setModulosGerenciaveis] = useState([]);
    const [grupoSelecionado, setGrupoSelecionado] = useState(null);
    const wasNovoGrupoOpen = useRef(false);

    const managedModulos = useMemo(
        () => new Set(modulosGerenciaveis.filter(Boolean)),
        [modulosGerenciaveis]
    );

    const fetchGrupos = useCallback(async () => {
        setLoading(true);

        try {
            const [gruposRes, modulosRes] = await Promise.all([
                api.get(LIST_GRUPOS_URL),
                api.get(LIST_MODULOS_URL),
            ]);
            setGrupos(Array.isArray(gruposRes.data) ? gruposRes.data : []);
            setModulosGerenciaveis(Array.isArray(modulosRes.data) ? modulosRes.data : []);
        } catch (error) {
            console.error(error);
            setGrupos([]);
            setModulosGerenciaveis([]);
            notifyError(error?.response?.data?.message || "Erro ao carregar grupos.");
        } finally {
            setLoading(false);
        }
    }, [api, notifyError]);

    useEffect(() => {
        fetchGrupos();
    }, [fetchGrupos]);

    const handleClose = () => {
        closeModal(MODAL_ID);
    };

    const handleAfterChange = useCallback(async () => {
        await fetchGrupos();
        onSaved?.();
    }, [fetchGrupos, onSaved]);

    useEffect(() => {
        const isNovoOpen = isModalOpen(MODAL_NOVO_GRUPO_ID);

        if (wasNovoGrupoOpen.current && !isNovoOpen) {
            handleAfterChange();
        }

        wasNovoGrupoOpen.current = isNovoOpen;
    }, [handleAfterChange, isModalOpen]);

    const abrirNovoGrupo = () => {
        openModal(MODAL_NOVO_GRUPO_ID);
    };

    const abrirEdicao = (grupo) => {
        setGrupoSelecionado(grupo);
        openModal(MODAL_EDITAR_GRUPO_ID);
    };

    const abrirDelete = (grupo) => {
        setGrupoSelecionado(grupo);
        openModal(MODAL_DELETE_GRUPO_ID);
    };

    return (
        <>
            <Overlay state changeContent={handleClose}>
                <Modal header close={handleClose} size="lg">
                    <Container>
                        <Header>
                            <Titulo>Gerenciar grupos</Titulo>
                            <BotaoNovo type="button" onClick={abrirNovoGrupo}>
                                <FiPlus size={16} />
                                NOVO GRUPO
                            </BotaoNovo>
                        </Header>

                        <TabelaWrapper>
                            {loading ? (
                                <Vazio>Carregando grupos...</Vazio>
                            ) : grupos.length === 0 ? (
                                <Vazio>Nenhum grupo encontrado.</Vazio>
                            ) : (
                                <Tabela>
                                    <thead>
                                        <tr>
                                            <th>Grupo</th>
                                            <th className="modulo">Módulo</th>
                                            <th className="numero">Permissões</th>
                                            <th className="numero">Usuários</th>
                                            <th className="acoes">Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {grupos.map((grupo) => {
                                            const podeApagar =
                                                managedModulos.size === 0
                                                    ? false
                                                    : managedModulos.has(grupo.modulo);

                                            return (
                                                <tr key={grupo.id}>
                                                    <td>{grupo.nome}</td>
                                                    <td className="modulo">{grupo.modulo || "—"}</td>
                                                    <td className="numero">{grupo.qtd_permissoes ?? 0}</td>
                                                    <td className="numero">{grupo.qtd_usuarios ?? 0}</td>
                                                    <td className="acoes">
                                                        <AcoesCelula>
                                                            <BotaoAcao
                                                                type="button"
                                                                title="Editar"
                                                                onClick={() => abrirEdicao(grupo)}
                                                            >
                                                                <FiEdit2 size={14} />
                                                            </BotaoAcao>
                                                            {podeApagar && (
                                                                <BotaoAcao
                                                                    type="button"
                                                                    $danger
                                                                    title="Remover"
                                                                    onClick={() => abrirDelete(grupo)}
                                                                >
                                                                    <FiTrash2 size={14} />
                                                                </BotaoAcao>
                                                            )}
                                                        </AcoesCelula>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Tabela>
                            )}
                        </TabelaWrapper>
                    </Container>
                </Modal>
            </Overlay>

            {isModalOpen(MODAL_EDITAR_GRUPO_ID) && grupoSelecionado && (
                <ModalEditarGrupo grupo={grupoSelecionado} onSaved={handleAfterChange} />
            )}

            {isModalOpen(MODAL_DELETE_GRUPO_ID) && grupoSelecionado && (
                <ModalConfirmDeleteGrupo
                    grupo={grupoSelecionado}
                    onDeleted={handleAfterChange}
                />
            )}
        </>
    );
};

export default ModalGerenciarGrupos;
